import GlobalElementsPage from "@bevco/Components/GlobalElementsPage";
import { setGA4SapNumberEvent } from "@bevco/Store/Handlers/Utilities/CustomTracking";
import { removeMetadata, setContactUsPageMetadata, setHomePageMetadata, setHrefLangMetadata, setRequestQuotePageMetadata, setStandardPageMetadata } from "@bevco/Utilities/Metadata";
import { captureInitialSiteLoadQueryIfNeeded } from "@bevco/Utilities/initialSiteLoadQuery";
import { trackPageChange } from "@bevco/Utilities/tracking";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Page, { PageProps } from "@insite/mobius/Page";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { ReactNode } from "react";
import { connect } from "react-redux";
import styled from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { session } = state.context;

    const currentBillTo = getBillToState(state, session.billToId).value;
    const customerNumber = session.billToId == null ? "54080" : currentBillTo?.customerNumber?.replace(/^0+/, "");
    const currentUrl = `${state.context.website.uri.split("/api")[0]}${state.data.pages.location.pathname}`;
    const HrefLangSettings = getSettingsCollection(state).hrefLangSettings;

    return {
        customerNumber,
        currentPage: getCurrentPage(state),
        currentPageType: getCurrentPage(state).type,
        location: getLocation(state),
        page: getCurrentPage(state),
        currentUrl,
        HrefLangSettings,
    };
};

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}

type Props = PageLayoutProps & ReturnType<typeof mapStateToProps>;


const LOCALE_RE = /^[a-z]{2}$/; // tweak if you only want a fixed set like /^(fr|es|en|de)$/

function getCanonicalBrandPath(pathname: string): string | null {
    const segments = pathname.split("/").filter(Boolean); 

    if (segments.length === 0) return null;

    const localeIndices = segments.map((seg, i) => (LOCALE_RE.test(seg) ? i : -1)).filter(i => i !== -1);

    const brandsIdx = segments.indexOf("brands");
    const hasBrand = brandsIdx !== -1 && segments[brandsIdx + 1];

    if (!hasBrand) return null;

    let locale: string | null = null;
    if (LOCALE_RE.test(segments[0])) {
        locale = segments[0];
    } else if (localeIndices.length > 0) {
        locale = segments[localeIndices[0]];
    }

    if (!locale) return null;

    const brandSlug = segments[brandsIdx + 1];

    const canonical = `/${locale}/brands/${brandSlug}/`;

    const localeCount = segments.filter(s => s === locale).length;
    const alreadyCanonical = pathname.replace(/\/+$/, "") === canonical.replace(/\/+$/, "");

    if (alreadyCanonical && localeCount === 1) return null;

    return canonical;
}

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showCompactHeader,
    compactHeader,
    showSignInOverlay,
    signInOverlay,
    showStorePickupOverlay,
    storePickupOverlay,
    showAddressBookOverlay,
    addressBookOverlay,

    /* Verndale Custom Feature - New variables */
    customerNumber,
    currentPage,
    currentPageType,
    location,
    page,    
    currentUrl,
    HrefLangSettings,
}: Props) => {
    /* Verndale Custom Feature - Add style for wrapper page */
    const wrapperStyle: InjectableCss = {};

    // Verndale: capture landing URL querystring once per device (any entry page); persists until clearInitialSiteLoadQuery()
    React.useEffect(() => {
        captureInitialSiteLoadQueryIfNeeded();
    }, []);

    // Verndale Customization: Remove metadata when the page changes
    React.useEffect(() => {
        const handleRemoveMetadata = () => {
            if (currentPage.type === "NullPage") {
                return;
            }

            if (currentPage.type !== "CategoryDetailsPage") {
                removeMetadata("categoryListItemList");
                removeMetadata("categoryListBreadCrumbList");
                removeMetadata("categoryDetailOrganization");
            }

            if (currentPage.type === "HomePage") {
                setHomePageMetadata({ 
                    currentUrl });
            }else {
                removeMetadata("homePage_OrganizationSchema");
                removeMetadata("homePage_ItemListSchema");
            }

            if (currentPage.type !== "ProductListPage") {
                removeMetadata("productList_itemListSchema");
                removeMetadata("productList_ProductSchema");
                removeMetadata("productList_BreadCrumbListSchema");
                removeMetadata("productList_OrganizationSchema");
            }

            if (currentPage.type !== "ProductDetailsPage") {                
                removeMetadata("productDetail_ProductSchema");
                removeMetadata("productDetail_BreadCrumbListSchema");
                removeMetadata("productDetail_OrganizationSchema");
            }

            if (currentPage.type !== "BrandsPage") {
                removeMetadata("brandList_BreadCrumbListSchema");
                removeMetadata("brandList_ItemListSchema_0");
                removeMetadata("brandList_ItemListSchema_1");
                removeMetadata("brandList_ItemListSchema_2");
                removeMetadata("brandList_ItemListSchema_3");
                removeMetadata("brandList_OrganizationSchema");
            }

            if (currentPage.type !== "LocationFinderPage") {
                removeMetadata("locationFinderPage_OrganizationSchema");
                removeMetadata("locationFinderPage_BreadCrumbListSchema");
                removeMetadata("locationFinderPage_ItemListSchema");
                removeMetadata("locationFinderPage_LocationBusinessSchema");
            }

            if(currentPage.type !== "Page"){
                removeMetadata("standardPage_OrganizationSchema");
                removeMetadata("standardPage_BreadCrumbListSchema");
            }
        };

        const addMetadata = () => {
            if (currentPage.type === "NullPage") {
                return;
            }

            if((HrefLangSettings as any).hreflangList && (HrefLangSettings as any).hreflangList.length > 0) {
                setHrefLangMetadata({
                    hreflangList: (HrefLangSettings as any).hreflangList,
                    currentUrl,
                    currentPage,
                    location,
                });
            }

            if(!currentPage.fields.urlSegment){
                removeMetadata("contactUsPage_OrganizationSchema");
                removeMetadata("contactUsPage_BreadCrumbListSchema");
                return;
            }

            // Contact US SEO schema
            if (currentPage.fields.urlSegment.toLowerCase().includes("contact-us")) {
                setContactUsPageMetadata({
                    currentUrl, currentPage
                });
            }else{
                removeMetadata("contactUsPage_OrganizationSchema");
                removeMetadata("contactUsPage_BreadCrumbListSchema");
            }

            // Request a Quote SEO schema
            if (currentPage.fields.urlSegment.toLowerCase().includes("request-a-quote")) {
                setRequestQuotePageMetadata({
                    currentUrl, currentPage
                });
            }else{
                removeMetadata("requestQuotePage_OrganizationSchema");
                removeMetadata("requestQuotePage_BreadCrumbListSchema");
            }

            if(currentPage.type === "Page" || currentPage.type === "BlogDetailPage"){
                setStandardPageMetadata({
                    currentUrl, currentPage
                });
            }else{
                removeMetadata("standardPage_OrganizationSchema");
                removeMetadata("standardPage_BreadCrumbListSchema");
            }
        };

        setTimeout(() => {
            handleRemoveMetadata();
            addMetadata();
        }, 500);

        return () => {
            handleRemoveMetadata();
            addMetadata();
        };
    }, [currentPage]);

    React.useEffect(() => {
        if (customerNumber && currentPageType !== "NullPage") {
            setGA4SapNumberEvent(customerNumber);
        }
    }, [customerNumber, currentPageType]);

    React.useEffect(() => {
        const url = location.pathname + location.search;
        const segments = url.split("/").filter(Boolean);
        const firstSegment = segments[0];
        const secondSegment = segments[1];
        const isCategory = (firstSegment === "wire-and-cable" || firstSegment === "wire-management") && !secondSegment;
        const isSubcategory = (firstSegment === "wire-and-cable" || firstSegment === "wire-management") && secondSegment; 

        if (isCategory || isSubcategory) {
            trackPageChange("CategoryDetailsPage");
        }

        
       /*  const canonical = getCanonicalBrandPath(location.pathname);
        if (canonical && canonical !== location.pathname) {
            if (typeof window !== "undefined") {
                window.history.replaceState(null, "", canonical + (location.search || ""));
                window.location.reload();
            }
        } */
    }, [location]);

    return (
        <PageLayoutWrapper>
            {showHeader && header}
            {showCompactHeader && compactHeader}
            {/* Verndale Custom Feature - Wrap content editor pages to show global components / elements */}
            <StyledWrapper {...wrapperStyle}>
                {showBreadcrumbs && (
                    <Page as="div">
                        <PageBreadcrumbs />
                    </Page>
                )}
                {pageContent}
                <GlobalElementsPage />
            </StyledWrapper>
            {showFooter && footer}
            {showSignInOverlay && signInOverlay}
            {showStorePickupOverlay && storePickupOverlay}
            {showAddressBookOverlay && addressBookOverlay}
            <VisuallyHidden>
                <Typography id="tst_userNav_customerNumber">{customerNumber}</Typography>
            </VisuallyHidden>
        </PageLayoutWrapper>
    );
};

const PageLayoutWrapper = styled.div`
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    > main {
        flex: 1 1 0%;
    }

    @media print {
        display: block;
    }
`;

export default connect(mapStateToProps)(PageLayout as any);
